import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  TextField,
  MenuItem,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  CircularProgress,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import GetAppIcon from "@mui/icons-material/GetApp";
import RefundIcon from "@mui/icons-material/AttachMoney";
import color from "../../components/utils/Colors";
import { useSnackbar } from "../../components/utils/SnackBar";
import { getUserPayments, getDataFromTable } from "../../services/services";
import { useUser } from "../../services/Context/UserContext";
import { AttachMoney, CurrencyRupee } from "@mui/icons-material";

interface Purchase {
  id: string;
  orderNumber: string;
  courseName: string;
  instructor: string;
  purchaseDate: string;
  amount: number;
  currency: string;
  status: "completed" | "pending" | "refunded" | string;
  paymentMethod: string;
  invoice: string;
  transactionId?: string;
}

const PurchasesHistory: React.FC = () => {
  const showSnackbar = useSnackbar();
  const { user } = useUser();
  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const [loading, setLoading] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState<Purchase | null>(
    null,
  );
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!user?.id) return;
    setLoading(true);
    getUserPayments(user.id)
      .then(async (res: any) => {
        const data: any[] = res?.data?.data ?? [];
        const mapped: Purchase[] = await Promise.all(
          data.map(async (item: any) => {
            let courseRes;
            console.log(item);
            if (item.courseId) {
              courseRes = await getDataFromTable("course", {
                id: item.courseId,
              });
            }
            const course = courseRes?.data?.data?.[0] || item?.course || {};
            return {
              id: String(item.id ?? ""),
              orderNumber: item.orderId ?? `ORD-${item.id}`,
              courseName:
                course.title ??
                course.courseName ??
                `Course #${item.courseId ?? ""}`,
              instructor: course.teachersName ?? "",
              purchaseDate: item.createdAt ?? item.updatedAt ?? "",
              amount: item.amount ?? 0,
              currency: course.unitOfPrice ?? item.currency ?? "USD",
              status: (item.status ?? "pending") as Purchase["status"],
              paymentMethod: item.modeOfPayment ?? "N/A",
              invoice: item.transactionId ?? `INV-${item.id}`,
              transactionId: item.transactionId,
            };
          }),
        );
        setPurchases(mapped);
      })
      .catch(() => {
        showSnackbar("Failed to load purchase history");
      })
      .finally(() => setLoading(false));
  }, [user?.id]);

  const filteredPurchases = purchases.filter((purchase) => {
    const matchesSearch =
      (purchase.courseName || "")
        .toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
      (purchase.orderNumber || "")
        .toLowerCase()
        .includes(searchQuery.toLowerCase());
    const matchesFilter =
      filterStatus === "all" || purchase.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const getStatusColor = (status: string): any => {
    switch (status) {
      case "success":
      case "completed":
        return "success";
      case "pending":
        return "warning";
      case "failed":
      case "refunded":
        return "error";
      default:
        return "default";
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "success":
      case "completed":
        return "✓ Success";
      case "pending":
        return "⏳ Pending";
      case "failed":
      case "refunded":
        return "↩ Failed/Refunded";
      default:
        return status;
    }
  };

  const handleViewDetails = (purchase: Purchase) => {
    setSelectedPurchase(purchase);
    setDetailsDialogOpen(true);
  };

  const handleDownloadInvoice = (purchase: Purchase) => {
    showSnackbar(`Invoice ${purchase.invoice} downloaded successfully`);
  };

  const handleRequestRefund = (purchase: Purchase) => {
    showSnackbar(
      `Refund request for ${purchase.courseName} submitted. Check your email.`,
    );
  };

  const totalSpent = purchases
    .filter((p) => p.status === "completed" || p.status === "success")
    .reduce((sum, p) => sum + p.amount, 0);

  const completedPurchases = purchases.filter(
    (p) => p.status === "completed" || p.status === "success",
  ).length;

  return (
    <Box
      sx={{ minHeight: "100vh", background: "transparent", paddingY: "40px" }}
    >
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ marginBottom: "40px" }}>
          <Typography
            variant="h4"
            sx={{
              color: color.textColor,
              fontWeight: "bold",
              marginBottom: "8px",
            }}
          >
            Purchases History
          </Typography>
          <Typography variant="body1" sx={{ color: "#666" }}>
            Manage your course purchases and invoices
          </Typography>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ marginBottom: "40px" }}>
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)" }}>
              <CardContent sx={{ padding: "24px", textAlign: "center" }}>
                <Typography
                  variant="h4"
                  sx={{
                    color: color.firstColor,
                    fontWeight: "bold",
                    marginBottom: "4px",
                  }}
                >
                  {completedPurchases}
                </Typography>
                <Typography variant="body2" sx={{ color: "#999" }}>
                  Courses Purchased
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)" }}>
              <CardContent sx={{ padding: "24px", textAlign: "center" }}>
                <Typography
                  variant="h4"
                  sx={{
                    color: color.thirdColor,
                    fontWeight: "bold",
                    marginBottom: "4px",
                  }}
                >
                  {purchases.length > 0 && purchases[0].currency === "INR" ? (
                    <CurrencyRupee sx={{ fontSize: 30, fontWeight: 500 }} />
                  ) : (
                    "$"
                  )}
                  {totalSpent.toFixed(2)}
                </Typography>
                <Typography variant="body2" sx={{ color: "#999" }}>
                  Total Spent
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)" }}>
              <CardContent sx={{ padding: "24px", textAlign: "center" }}>
                <Typography
                  variant="h4"
                  sx={{
                    color: "#ff9800",
                    fontWeight: "bold",
                    marginBottom: "4px",
                  }}
                >
                  {purchases.filter((p) => p.status === "pending").length}
                </Typography>
                <Typography variant="body2" sx={{ color: "#999" }}>
                  Pending Orders
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Filters */}
        <Card
          sx={{
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)",
            marginBottom: "30px",
          }}
        >
          <CardContent sx={{ padding: "24px" }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <label htmlFor="status">Search</label>
                <br />
                <TextField
                  fullWidth
                  placeholder="Search by order number or course name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  size="small"
                  className="input-root2"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <label htmlFor="status">Filter by Status</label>
                <br />
                <TextField
                  fullWidth
                  select
                  id="status"
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  size="small"
                  className="input-root2"
                >
                  <MenuItem value="all">All Orders</MenuItem>
                  <MenuItem value="success">Success / Completed</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="failed">Failed / Refunded</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Purchases Table */}
        <Card
          sx={{
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)",
            borderRadius: "12px",
            overflow: "hidden",
          }}
        >
          {loading ? (
            <CardContent sx={{ textAlign: "center", padding: "60px 20px" }}>
              <CircularProgress sx={{ color: color.firstColor }} />
            </CardContent>
          ) : filteredPurchases.length > 0 ? (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow sx={{ background: "#f5f5f5" }}>
                    <TableCell
                      sx={{ fontWeight: "bold", color: color.textColor }}
                    >
                      Order #
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: "bold", color: color.textColor }}
                    >
                      Course Name
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: "bold", color: color.textColor }}
                    >
                      Date
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        color: color.textColor,
                        textAlign: "right",
                      }}
                    >
                      Amount
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: "bold", color: color.textColor }}
                    >
                      Status
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        color: color.textColor,
                        textAlign: "center",
                      }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredPurchases.map((purchase) => (
                    <TableRow
                      key={purchase.id}
                      sx={{ "&:hover": { background: "#f9f9f9" } }}
                    >
                      <TableCell
                        sx={{ fontWeight: "500", color: color.firstColor }}
                      >
                        {purchase.orderNumber}
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: "500", color: color.textColor }}
                          >
                            {purchase.courseName}
                          </Typography>
                          {purchase.instructor && (
                            <Typography
                              variant="caption"
                              sx={{ color: "#999" }}
                            >
                              {purchase.instructor}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: "#666" }}>
                          {purchase.purchaseDate
                            ? new Date(
                                purchase.purchaseDate,
                              ).toLocaleDateString()
                            : "N/A"}
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={{
                          textAlign: "right",
                          fontWeight: "500",
                          color: color.textColor,
                        }}
                      >
                        {purchase.currency === "INR" ? (
                          <CurrencyRupee sx={{ fontSize: 20 }} />
                        ) : (
                          <AttachMoney sx={{ fontSize: 22 }} />
                        )}{" "}
                        {purchase.amount.toFixed(2)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusLabel(purchase.status)}
                          color={getStatusColor(purchase.status)}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell sx={{ textAlign: "center" }}>
                        <Button
                          size="small"
                          startIcon={<VisibilityIcon />}
                          sx={{
                            color: color.firstColor,
                            textTransform: "none",
                            marginRight: "4px",
                          }}
                          onClick={() => handleViewDetails(purchase)}
                        >
                          Details
                        </Button>
                        <Button
                          size="small"
                          startIcon={<GetAppIcon />}
                          sx={{
                            color: color.firstColor,
                            textTransform: "none",
                          }}
                          onClick={() => handleDownloadInvoice(purchase)}
                        >
                          Invoice
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <CardContent sx={{ textAlign: "center", padding: "60px 20px" }}>
              <Typography
                variant="h6"
                sx={{ color: "#999", marginBottom: "10px" }}
              >
                No purchases found
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "#bbb", marginBottom: "20px" }}
              >
                {searchQuery || filterStatus !== "all"
                  ? "Try adjusting your filters or search"
                  : "You haven't purchased any courses yet"}
              </Typography>
              <Button
                variant="contained"
                sx={{
                  background: color.firstColor,
                  textTransform: "none",
                  fontWeight: "bold",
                  color: "white",
                  "&:hover": { background: color.forthColor },
                }}
              >
                Explore Courses
              </Button>
            </CardContent>
          )}
        </Card>
      </Container>

      {/* Purchase Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: color.textColor, fontWeight: "bold" }}>
          Purchase Details
        </DialogTitle>
        <DialogContent sx={{ paddingY: "20px" }}>
          {selectedPurchase && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              <Box>
                <Typography
                  variant="caption"
                  sx={{ color: "#999", fontWeight: "bold" }}
                >
                  ORDER NUMBER
                </Typography>
                <Typography variant="body1" sx={{ color: color.textColor }}>
                  {selectedPurchase.orderNumber}
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  sx={{ color: "#999", fontWeight: "bold" }}
                >
                  COURSE
                </Typography>
                <Typography variant="body1" sx={{ color: color.textColor }}>
                  {selectedPurchase.courseName}
                </Typography>
              </Box>
              {selectedPurchase.instructor && (
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ color: "#999", fontWeight: "bold" }}
                  >
                    INSTRUCTOR
                  </Typography>
                  <Typography variant="body1" sx={{ color: color.textColor }}>
                    {selectedPurchase.instructor}
                  </Typography>
                </Box>
              )}
              <Box>
                <Typography
                  variant="caption"
                  sx={{ color: "#999", fontWeight: "bold" }}
                >
                  PURCHASE DATE
                </Typography>
                <Typography variant="body1" sx={{ color: color.textColor }}>
                  {selectedPurchase.purchaseDate
                    ? new Date(
                        selectedPurchase.purchaseDate,
                      ).toLocaleDateString()
                    : "N/A"}
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  sx={{ color: "#999", fontWeight: "bold" }}
                >
                  PAYMENT METHOD
                </Typography>
                <Typography variant="body1" sx={{ color: color.textColor }}>
                  {selectedPurchase.paymentMethod}
                </Typography>
              </Box>
              {selectedPurchase.transactionId && (
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ color: "#999", fontWeight: "bold" }}
                  >
                    TRANSACTION ID
                  </Typography>
                  <Typography variant="body1" sx={{ color: color.textColor }}>
                    {selectedPurchase.transactionId}
                  </Typography>
                </Box>
              )}
              <Box>
                <Typography
                  variant="caption"
                  sx={{ color: "#999", fontWeight: "bold" }}
                >
                  INVOICE
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: color.firstColor, fontWeight: "bold" }}
                >
                  {selectedPurchase.invoice}
                </Typography>
              </Box>
              <Box
                sx={{
                  background: color.secondColor,
                  padding: "16px",
                  borderRadius: "8px",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ color: color.textColor, fontWeight: "bold" }}
                  >
                    Total Amount
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{ color: color.firstColor, fontWeight: "bold" }}
                  >
                    {selectedPurchase.currency}{" "}
                    {selectedPurchase.amount.toFixed(2)}
                  </Typography>
                </Box>
              </Box>
              <Chip
                label={getStatusLabel(selectedPurchase.status)}
                color={getStatusColor(selectedPurchase.status)}
                variant="outlined"
                sx={{ alignSelf: "flex-start" }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: "20px" }}>
          <Button
            onClick={() => setDetailsDialogOpen(false)}
            sx={{ color: "#999" }}
          >
            Close
          </Button>
          {selectedPurchase &&
            (selectedPurchase.status === "completed" ||
              selectedPurchase.status === "success") && (
              <>
                <Button
                  variant="outlined"
                  startIcon={<GetAppIcon />}
                  sx={{
                    borderColor: color.firstColor,
                    color: color.firstColor,
                    textTransform: "none",
                  }}
                  onClick={() => {
                    handleDownloadInvoice(selectedPurchase);
                    setDetailsDialogOpen(false);
                  }}
                >
                  Download Invoice
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<RefundIcon />}
                  sx={{
                    borderColor: "#ff9800",
                    color: "#ff9800",
                    textTransform: "none",
                  }}
                  onClick={() => {
                    handleRequestRefund(selectedPurchase);
                    setDetailsDialogOpen(false);
                  }}
                >
                  Request Refund
                </Button>
              </>
            )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PurchasesHistory;
